import { computed, ref } from 'vue';

import type { Config, EditorMode, TaxonomieType } from '@/types/editor';

type OverrideMainActionButton = {
  text: string;
  action: () => void;
};

const overrideMainActionButton = ref<OverrideMainActionButton>();
const mode = ref<EditorMode>('auto');
const configurationAuto = ref<Config>({
  taxonomies: [],
  products: [],
});
const configurationManual = ref<Config>({
  taxonomies: [],
  products: [],
});

const currentConfiguration = computed<Config>({
  get() {
    return mode.value === 'auto' ? configurationAuto.value : configurationManual.value;
  },
  set(newValue: Config) {
    if (mode.value === 'auto') {
      configurationAuto.value = newValue;
    }
    else {
      configurationManual.value = newValue;
    }
  },
});

export function useEditor() {
  const taxonomieTypeToFilter = (type: TaxonomieType) => {
    switch (type) {
      case 'THEMES': return 'taxonomyThemes';
      case 'AUDIENCES': return 'taxonomyAudiences';
      case 'INTERNALS': return 'taxonomyInternals';
      case 'PLACES': return 'taxonomyPlaces';
      case 'TYPES': return 'taxonomyTypes';
    }
  };

  const configToFilter = (config: Config) => {
    const filters = [];
    (config.taxonomies ?? []).forEach((entry) => {
      filters.push({
        filterProperties: {
          type: taxonomieTypeToFilter(entry.type),
        },
        selection: entry.value,
      });
    });
    if (config.products.length > 0) {
      filters.push({
        filterProperties: { type: 'productIds' },
        selection: config.products,
      });
    }
    filters.push({
      filterProperties: {
        type: 'publication_states',
        target: 'product',
      },
      selection: ['PUBLISHED'],
    });
    return filters;
  };

  const clear = () => {
    configurationAuto.value.taxonomies = [];
    configurationManual.value.products = [];
  };

  const generateConfig = () => {
    return JSON.stringify(currentConfiguration.value);
  };

  const loadConfig = (config: Partial<Config>) => {
    if (config.products && config.products.length > 0) {
      configurationManual.value.products = config.products;
      mode.value = 'manual';
    }
    if (config.taxonomies && config.taxonomies.length > 0) {
      configurationAuto.value.taxonomies = config.taxonomies;
      mode.value = 'auto';
    }
  };

  return {
    mode,
    currentConfiguration,
    configurationAuto,
    configurationManual,
    overrideMainActionButton,

    configToFilter,
    clear,
    generateConfig,
    loadConfig,
  };
}
